import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { TrendingUp, TrendingDown, Coins, ArrowRight, Sparkles } from "lucide-react";
import Navbar from "../components/Navbar";
import { useAuth } from "../context/AuthContext";
import DailyMovers from "../components/DailyMovers";
import CanSlimWidget from "../components/CanSlimWidget";
import StocksToWatch from "../components/StocksToWatch";
import CryptoMarketsBoard from "../components/CryptoMarketsBoard";
import MarketSentimentWidget from "../components/MarketSentimentWidget";
import WhatsChangedFeed from "../components/WhatsChangedFeed";
import TodaysLessonCard from "../components/TodaysLessonCard";
import api from "../lib/api";
import { getPref, setPref } from "../lib/prefs";

// Crypto highlights live in the streaming CryptoMarketsBoard above; this section now
// focuses on big-cap equities so the dashboard isn't duplicating BTC/ETH/SOL twice.
const HIGHLIGHTS = [
  { type: "stock", symbol: "AAPL" },
  { type: "stock", symbol: "TSLA" },
  { type: "stock", symbol: "NVDA" },
  { type: "stock", symbol: "MSFT" },
  { type: "stock", symbol: "AMZN" },
  { type: "stock", symbol: "GOOGL" },
];

// Dashboard-wide timeframe toggle. Backend `range` keys → user-facing labels.
const TIMEFRAMES = [
  { key: "1D", short: "1D", label: "Daily" },
  { key: "1W", short: "1W", label: "Weekly" },
  { key: "1M", short: "1M", label: "Monthly" },
  { key: "YTD", short: "YTD", label: "YTD" },
];

const TF_PREF_KEY = "dashboard_tf";

function MiniCard({ asset, data, loading, rangeLabel }) {
  const isUp = (data?.change_pct ?? 0) >= 0;
  const trendColor = isUp ? "#5C8D73" : "#C66B59";
  return (
    <Link
      to={`/asset/${asset.type}/${asset.symbol}`}
      data-testid={`market-card-${asset.symbol}`}
      className="group rounded-2xl bg-white border border-[#EAE6E1] p-5 transition-soft hover:-translate-y-1 hover:shadow-[0_12px_40px_rgba(42,47,43,0.06)] block"
    >
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="flex items-center gap-2 mb-0.5">
            <div
              className="w-7 h-7 rounded-full flex items-center justify-center"
              style={{ backgroundColor: asset.type === "stock" ? "#547A8F15" : "#D18E6315" }}
            >
              {asset.type === "stock" ? (
                <TrendingUp className="w-3.5 h-3.5 text-[#547A8F]" strokeWidth={1.75} />
              ) : (
                <Coins className="w-3.5 h-3.5 text-[#D18E63]" strokeWidth={1.75} />
              )}
            </div>
            <span className="font-mono-ticker font-semibold text-sm text-[#2A2F2B]">
              {asset.symbol}
            </span>
          </div>
          <div className="text-xs text-[#6B7068]">{data?.name || "—"}</div>
        </div>
        <ArrowRight className="w-4 h-4 text-[#6B7068] group-hover:text-[#2A2F2B] group-hover:translate-x-0.5 transition-soft" strokeWidth={1.5} />
      </div>
      {loading ? (
        <div className="h-10 bg-[#F9F8F6] rounded animate-pulse" />
      ) : data ? (
        <>
          <div className="font-mono-ticker text-2xl text-[#2A2F2B]">
            ${data.price < 1 ? data.price.toFixed(4) : data.price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
          <div className="flex items-center gap-1 mt-1 text-xs font-mono-ticker font-semibold" style={{ color: trendColor }}>
            {isUp ? <TrendingUp className="w-3 h-3" strokeWidth={2.5} /> : <TrendingDown className="w-3 h-3" strokeWidth={2.5} />}
            {isUp ? "+" : ""}{data.change_pct.toFixed(2)}%
            <span className="text-[#6B7068] font-normal ml-1">{rangeLabel}</span>
          </div>
        </>
      ) : (
        <div className="text-xs text-[#6B7068]">Unable to load</div>
      )}
    </Link>
  );
}

function TimeframeToggle({ value, onChange }) {
  return (
    <div
      className="inline-flex items-center bg-white border border-[#EAE6E1] rounded-full p-1 gap-0.5"
      data-testid="dashboard-timeframe-toggle"
      role="tablist"
      aria-label="Dashboard timeframe"
    >
      {TIMEFRAMES.map((tf) => {
        const active = tf.key === value;
        return (
          <button
            key={tf.key}
            type="button"
            role="tab"
            aria-selected={active}
            data-testid={`dashboard-timeframe-${tf.key}`}
            onClick={() => onChange(tf.key)}
            className={`px-4 py-1.5 text-xs font-medium uppercase tracking-[0.18em] rounded-full transition-soft ${
              active
                ? "bg-[#2A2F2B] text-white shadow-sm"
                : "text-[#6B7068] hover:text-[#2A2F2B]"
            }`}
          >
            {tf.label}
          </button>
        );
      })}
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [marketData, setMarketData] = useState({});
  const [loading, setLoading] = useState(true);
  const [timeframe, setTimeframe] = useState(() => {
    const saved = getPref(TF_PREF_KEY);
    if (saved && TIMEFRAMES.find((t) => t.key === saved)) return saved;
    return "1D";
  });

  const tfMeta = TIMEFRAMES.find((t) => t.key === timeframe) || TIMEFRAMES[0];

  // Persist timeframe choice across reloads
  useEffect(() => {
    setPref(TF_PREF_KEY, timeframe);
  }, [timeframe]);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    Promise.allSettled(
      HIGHLIGHTS.map((a) =>
        api
          .get(`/quote/${a.type}/${a.symbol}?range=${timeframe}`)
          .then((r) => ({ key: `${a.type}-${a.symbol}`, data: r.data }))
      )
    ).then((results) => {
      if (cancel) return;
      const map = {};
      results.forEach((r) => {
        if (r.status === "fulfilled") map[r.value.key] = r.value.data;
      });
      setMarketData(map);
      setLoading(false);
    });
    return () => {
      cancel = true;
    };
  }, [timeframe]);

  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      <Navbar />
      <main className="max-w-7xl mx-auto px-6 md:px-8 lg:px-12 py-10 md:py-14" data-testid="dashboard-page">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10 animate-fade-up">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#6B7068]">
              Welcome back
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl tracking-tighter font-medium text-[#2A2F2B] mt-1">
              Hey, {user?.name?.split(" ")[0] || "trader"}.
            </h1>
            <p className="text-[#6B7068] mt-2">
              Here's what's moving today across stocks and crypto.
            </p>
          </div>
          <div className="flex items-center gap-2 px-3 py-2 rounded-full bg-white border border-[#EAE6E1] text-sm">
            <Sparkles className="w-4 h-4 text-[#D18E63]" strokeWidth={1.75} />
            <span className="text-[#6B7068]">AI explanations available on every chart</span>
          </div>
        </div>

        <section className="mb-12" data-testid="todays-lesson-section">
          <TodaysLessonCard />
        </section>

        <section className="mb-12" data-testid="whats-changed-section">
          <WhatsChangedFeed />
        </section>

        <section className="mb-12">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 md:gap-4 mb-5">
            <div>
              <h2 className="font-heading text-2xl text-[#2A2F2B]">Movers</h2>
              <p className="text-xs text-[#6B7068] mt-0.5">
                Biggest {tfMeta.label.toLowerCase()} moves from your watchlists
              </p>
            </div>
            <TimeframeToggle value={timeframe} onChange={setTimeframe} />
          </div>
          <DailyMovers range={timeframe} rangeLabel={tfMeta.short} />
        </section>

        <section className="mb-12">
          <MarketSentimentWidget />
        </section>

        <section className="mb-12">
          <CryptoMarketsBoard />
        </section>

        <section className="mb-12">
          <StocksToWatch />
        </section>

        <section className="mb-12">
          <CanSlimWidget />
        </section>

        <section className="mb-12">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 md:gap-4 mb-5">
            <div>
              <h2 className="font-heading text-2xl text-[#2A2F2B]">Markets at a glance</h2>
              <p className="text-xs text-[#6B7068] mt-0.5">
                {tfMeta.label} change over the {
                  tfMeta.key === "YTD" ? "year so far" :
                  tfMeta.key === "1D" ? "last trading day" :
                  tfMeta.key === "1W" ? "last 5 trading days" :
                  "last month"
                }
              </p>
            </div>
            <Link to="/watchlist" className="text-sm text-[#547A8F] hover:text-[#426071]" data-testid="view-watchlist-link">
              View watchlist →
            </Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {HIGHLIGHTS.map((a) => (
              <MiniCard
                key={`${a.type}-${a.symbol}`}
                asset={a}
                data={marketData[`${a.type}-${a.symbol}`]}
                loading={loading}
                rangeLabel={tfMeta.short}
              />
            ))}
          </div>
        </section>

        <section className="rounded-3xl bg-white border border-[#EAE6E1] p-8 md:p-12 grid md:grid-cols-2 gap-8 items-center">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#547A8F]">
              New to charts?
            </div>
            <h3 className="font-heading text-3xl tracking-tight text-[#2A2F2B] mt-2 mb-3">
              Start with the fundamentals.
            </h3>
            <p className="text-[#6B7068] mb-5">
              Learn what candlesticks, volume, and trends really mean — at your own
              pace, with no jargon.
            </p>
            <Link to="/learn" data-testid="dashboard-learn-link">
              <button className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#2A2F2B] hover:bg-[#547A8F] text-white text-sm transition-soft">
                Open the learn hub
                <ArrowRight className="w-4 h-4" strokeWidth={2} />
              </button>
            </Link>
          </div>
          <div className="rounded-2xl bg-[#F9F8F6] p-6 border border-[#EAE6E1]">
            <ul className="space-y-3">
              {[
                ["What is a candlestick?", "1 min read"],
                ["Reading volume bars", "2 min read"],
                ["Trends, support & resistance", "3 min read"],
                ["Avoiding emotional trading", "2 min read"],
              ].map(([t, d]) => (
                <li key={t} className="flex items-center justify-between border-b border-[#EAE6E1] pb-3 last:border-0 last:pb-0">
                  <span className="text-sm text-[#2A2F2B]">{t}</span>
                  <span className="text-xs text-[#6B7068] font-mono-ticker">{d}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>
      </main>
    </div>
  );
}
